import { useAuth } from "@clerk/clerk-react";
import { Box, Button, Link, Skeleton, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useState } from "react";
import { AffiliateOrganization } from "schemas/dashboard";

import {
  AFFILIATE_TERMS_AND_CONDITIONS_LINK,
  COMMISSION_PERCENTAGE,
} from "constants/influencer";
import { fetcherAuth } from "utils/api";

import AffiliatePartnerView from "./AffiliatePartnerView";

export default function AffiliateProgram() {
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const [affiliateOrg, setAffiliateOrg] =
    useState<AffiliateOrganization | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchAffiliateOrg = async () => {
    if (!currentOrg?.id) return;
    try {
      setLoading(true);
      const { affiliateOrg } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/affiliate-organizations`,
        "GET",
      );
      setAffiliateOrg(affiliateOrg);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  const createAffiliateOrganization = async () => {
    try {
      setLoading(true);
      const { affiliateOrg } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/affiliate-organizations`,
        "POST",
      );
      setAffiliateOrg(affiliateOrg);
      setAlert("Successfully opted in to affiliate program", "success");
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(
    () => {
      fetchAffiliateOrg();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentOrg?.id],
  );

  if (loading) {
    return (
      <Box>
        <Skeleton height={40} />
        <Skeleton height={80} />
        <Skeleton height={40} />
      </Box>
    );
  }

  if (affiliateOrg?.isActive) {
    return <AffiliatePartnerView affiliateOrg={affiliateOrg} />;
  }

  return (
    <>
      <Typography variant="h4" gutterBottom>
        Join the Bento Affiliate Program
      </Typography>
      <Typography>
        Earn commission on every referral—starting as soon as you opt in! As a
        Bento affiliate, you'll enjoy exclusive perks:
      </Typography>
      <ul>
        <li>
          <Typography>
            {COMMISSION_PERCENTAGE}% commission on every referral
          </Typography>
        </li>
        <li>
          <Typography>
            Receive special bonuses ✨
            <ul>
              <li>Refer 5 paying users and earn an additional $50</li>
              <li>Refer 10 paying users and earn an additional $150</li>
              <li>Refer 20 paying users and earn an additional $500</li>
              <li>Refer 100 paying users and earn an additional $2500</li>
            </ul>
          </Typography>
        </li>
        <li>
          <Typography>
            Special discount codes to share (requires approval)
          </Typography>
        </li>
        <li>
          <Typography>Exclusive prizes for top performers</Typography>
        </li>
        <li>
          A chance to <strong>earn additional income</strong> by licensing your
          content about Bento for use in ads
        </li>
      </ul>
      <Typography paragraph>
        Click the button below to join (
        <Link target="_blank" href={AFFILIATE_TERMS_AND_CONDITIONS_LINK}>
          Terms and Conditions Apply
        </Link>
        )
      </Typography>

      <br />
      <Button
        disableElevation
        variant="contained"
        onClick={createAffiliateOrganization}
        sx={{ mt: 2 }}
      >
        Opt In
      </Button>
    </>
  );
}
